/**
 * Display configuration for bot metrics parameters.
 *
 * NVP serves every metric as a FLAT 1xN vector (the metadata says RowCount=1),
 * but semantically they are matrices: the wing 36-vectors are 6x6, the wing
 * 12-vectors are 2x6 (extension / retraction rows), rake and most HMI params
 * are 1x4, and the HMI quantile params are 5x4 (five quantiles per channel).
 * The true shape lives here because the NVP metadata does not carry it; the
 * Axes metadata field is empty until the boat side populates it, so column
 * headers are index numbers for now.
 *
 * That flat vector is COLUMN-major: the NVP wire format packs matrices by
 * column, and the client only un-packs them when the metadata declares
 * RowCount>1 — which the bot definitions never do — so what arrives (and what
 * the backend stores) is the raw column-major dump. Read rows out of it with
 * matrixRow(), never by slicing cols-sized chunks: on the 12-vectors a
 * row-major slice mixes both phases into each labelled row, and on the 6x6
 * matrices it transposes the result (invisible on the near-symmetric Pearson
 * matrix, wrong by 2-3x per cell on Cross RMSe).
 */

import type { components } from "@/api/generated/schema";

export type MetricsKind = components["schemas"]["MetricsKind"];

export type MetricParamEntry = components["schemas"]["MetricParamEntry"];

export interface MetricParamValue {
  values: (number | null)[]; // null = non-finite bot output, normalized server-side
  accuracy?: number;
  chpDataType?: string;
}

/** Typed NVP param entries -> lookup by name. */
export function paramsToMap(parameters: MetricParamEntry[]): Map<string, MetricParamValue> {
  return new Map(
    parameters.map((p) => [
      p.Name,
      {
        values: p.Value,
        accuracy: p.Accuracy ?? undefined,
        chpDataType: p.ChpDataType ?? undefined,
      },
    ]),
  );
}

export interface MetricDisplaySpec {
  label: string;
  rows: number;
  cols: number;
  rowLabels?: readonly string[];
  /** Shade cells by magnitude, naming which end of the range is the bad one. */
  heat?: "low-is-worst" | "high-is-worst";
  /** Draw the rows as a distribution above the values. "box" expects the five
   * quantile rows in ascending order. */
  plot?: "box";
}

export interface HeatScale {
  best: number;
  worst: number;
  /** 0 (best) .. 1 (worst), or null for a cell the scale leaves unpainted. */
  at(row: number, col: number): number | null;
}

/** Magnitude scale for one matrix, or null when there is nothing to shade.
 *
 * The domain is the matrix's own range because NVP carries no thresholds for
 * these metrics — a fixed scale would either wash out a clean run or cry wolf
 * over one. Two kinds of cell stay unpainted rather than land on an end of the
 * ramp they would misrepresent: the diagonal, where a channel is compared with
 * itself and is perfect by construction, and exact zeros, which is how an
 * unexercised channel reads across its whole row and column (shading that as
 * the best result would invent a measurement the bot never took).
 */
export function heatScale(
  values: (number | null)[],
  spec: MetricDisplaySpec,
): HeatScale | null {
  if (!spec.heat) return null;
  const isSquare = spec.rows === spec.cols;
  const shaded = (row: number, col: number): number | null => {
    if (isSquare && row === col) return null;
    const value = values[col * spec.rows + row];
    return value == null || value === 0 ? null : value;
  };

  const present: number[] = [];
  for (let row = 0; row < spec.rows; row++) {
    for (let col = 0; col < spec.cols; col++) {
      const value = shaded(row, col);
      if (value != null) present.push(value);
    }
  }
  if (present.length < 2) return null;
  const min = Math.min(...present);
  const max = Math.max(...present);
  if (min === max) return null;

  const lowIsWorst = spec.heat === "low-is-worst";
  return {
    best: lowIsWorst ? max : min,
    worst: lowIsWorst ? min : max,
    at(row, col) {
      const value = shaded(row, col);
      if (value == null) return null;
      const t = (value - min) / (max - min);
      return lowIsWorst ? 1 - t : t;
    },
  };
}

/** Fill for a heat level, as a mix of the mode's --heat step with the surface.
 * Floored at 10% so the best cell still reads as measured-and-shaded rather
 * than as one of the cells the scale skips. */
export function heatFill(level: number): string {
  return `color-mix(in oklab, var(--heat) ${(10 + 52 * level).toFixed(1)}%, transparent)`;
}

/** One display row out of a metric's flat column-major vector. Identity for
 * 1xN specs, so every caller can go through it. */
export function matrixRow(
  values: (number | null)[],
  spec: MetricDisplaySpec,
  row: number,
): (number | null)[] {
  return Array.from({ length: spec.cols }, (_, col) => values[col * spec.rows + row] ?? null);
}

/** One display column — contiguous in a column-major vector, which is why the
 * quantile matrices read as one distribution per channel. */
export function matrixCol(
  values: (number | null)[],
  spec: MetricDisplaySpec,
  col: number,
): (number | null)[] {
  return Array.from({ length: spec.rows }, (_, row) => values[col * spec.rows + row] ?? null);
}

export interface MetricsKindSpec {
  label: string;
  /** Which NVP the fetch reads from — drives the online-status dot. */
  nvp: "platform" | "wing";
  /** Present when the app can remote-start a run (HMIREC-style trigger).
   * Range mirrors the NVP metadata Min/Max on the duration param. */
  trigger?: { defaultDurationS: number; minS: number; maxS: number };
  params: readonly { name: string; spec: MetricDisplaySpec }[];
}

// The 12-vectors are grouped per channel (each channel's two phases sit in
// adjacent flat elements — a dead channel zeroes both), but which phase comes
// first inside the pair is not derivable from the data. Order here is the
// long-standing assumption, still unconfirmed by the boat side.
const EXT_RET = ["Extension", "Retraction"] as const;

const wingMatrix = (label: string, heat: MetricDisplaySpec["heat"]): MetricDisplaySpec => ({
  label,
  rows: 6,
  cols: 6,
  heat,
});
const wingVector = (label: string): MetricDisplaySpec => ({
  label,
  rows: 2,
  cols: 6,
  rowLabels: EXT_RET,
});
const quad = (label: string): MetricDisplaySpec => ({ label, rows: 1, cols: 4 });

// The boat evaluates the five-number summary online (build 0135) so the app can
// draw a distribution without ever holding the raw samples. Ascending, and the
// vector is column-major, so each channel's five quantiles sit contiguously.
const QUANTILE_ROWS = ["0%", "25%", "50%", "75%", "100%"] as const;
const quantiles = (label: string): MetricDisplaySpec => ({
  label,
  rows: 5,
  cols: 4,
  rowLabels: QUANTILE_ROWS,
  plot: "box",
});

export const METRICS_KINDS: Record<MetricsKind, MetricsKindSpec> = {
  wing_bot: {
    label: "Wing Bot",
    nvp: "wing",
    params: [
      { name: "BOT_PearsonMatrix", spec: wingMatrix("Pearson Matrix", "low-is-worst") },
      { name: "BOT_CrossRMSeMatrix", spec: wingMatrix("Cross RMSe Matrix", "high-is-worst") },
      { name: "BOT_StepPeakTime", spec: wingVector("Step Peak Time") },
      { name: "BOT_StepRiseTime", spec: wingVector("Step Rise Time") },
      { name: "BOT_StepSetTime", spec: wingVector("Step Settle Time") },
      { name: "BOT_OvsPerc", spec: wingVector("Overshoot %") },
      { name: "BOT_UndPerc", spec: wingVector("Undershoot %") },
      { name: "BOT_StepPeak", spec: wingVector("Step Peak") },
      { name: "BOT_StepRate", spec: wingVector("Step Rate") },
      { name: "BOT_RampDelay", spec: wingVector("Ramp Delay") },
      { name: "BOT_RampVelStd", spec: wingVector("Ramp Vel StdDev") },
      { name: "BOT_SSRMSe", spec: wingVector("Steady-State RMSe") },
    ],
  },
  rake_bot: {
    label: "Rake Bot",
    nvp: "platform",
    params: [
      { name: "BOT_StepPeakTime_Max", spec: quad("Step Peak Time (Max)") },
      { name: "BOT_StepPeakTime_Avg", spec: quad("Step Peak Time (Avg)") },
      { name: "BOT_StepRiseTime_Max", spec: quad("Step Rise Time (Max)") },
      { name: "BOT_StepRiseTime_Avg", spec: quad("Step Rise Time (Avg)") },
      { name: "BOT_StepSetTime_Max", spec: quad("Step Settle Time (Max)") },
      { name: "BOT_StepSetTime_Avg", spec: quad("Step Settle Time (Avg)") },
      { name: "BOT_OvsPerc_Max", spec: quad("Overshoot % (Max)") },
      { name: "BOT_OvsPerc_Avg", spec: quad("Overshoot % (Avg)") },
      { name: "BOT_UndPerc_Max", spec: quad("Undershoot % (Max)") },
      { name: "BOT_UndPerc_Avg", spec: quad("Undershoot % (Avg)") },
      { name: "BOT_StepPeak_Max", spec: quad("Step Peak (Max)") },
      { name: "BOT_StepPeak_Avg", spec: quad("Step Peak (Avg)") },
      { name: "BOT_StepRate_Max", spec: quad("Step Rate (Max)") },
      { name: "BOT_StepRate_Avg", spec: quad("Step Rate (Avg)") },
      { name: "BOT_RampDelay_Max", spec: quad("Ramp Delay (Max)") },
      { name: "BOT_RampDelay_Avg", spec: quad("Ramp Delay (Avg)") },
      { name: "BOT_RampVelStd_Max", spec: quad("Ramp Vel StdDev (Max)") },
      { name: "BOT_RampVelStd_Avg", spec: quad("Ramp Vel StdDev (Avg)") },
      { name: "BOT_SSRMSe_Max", spec: quad("Steady-State RMSe (Max)") },
      { name: "BOT_SSRMSe_Avg", spec: quad("Steady-State RMSe (Avg)") },
    ],
  },
  hmi_record: {
    label: "HMI Recording",
    nvp: "platform",
    trigger: { defaultDurationS: 20, minS: 1, maxS: 36000 },
    params: [
      { name: "HMIREC_Angle_Pilot_Drift", spec: quad("Angle Pilot Drift") },
      { name: "HMIREC_Angle_Wheel_Drift", spec: quad("Angle Wheel Drift") },
      { name: "HMIREC_Angle_Pilot_Range", spec: quad("Angle Pilot Range") },
      { name: "HMIREC_Angle_Wheel_Range", spec: quad("Angle Wheel Range") },
      { name: "HMIREC_Angle_Pilot_StdDev", spec: quad("Angle Pilot StdDev") },
      { name: "HMIREC_Angle_Wheel_StdDev", spec: quad("Angle Wheel StdDev") },
      { name: "HMIREC_Angle_Pilot_Quantiles", spec: quantiles("Angle Pilot Quantiles") },
      { name: "HMIREC_Angle_Wheel_Quantiles", spec: quantiles("Angle Wheel Quantiles") },
      // Build 0132 and earlier measured angular RATE instead. Kept so those
      // recordings still render in the cross-event history; a build-0135 read
      // simply has no such param and the row drops out.
      { name: "HMIREC_Rate_Pilot_StdDev", spec: quad("Rate Pilot StdDev") },
      { name: "HMIREC_Rate_Wheel_StdDev", spec: quad("Rate Wheel StdDev") },
      { name: "HMIREC_Rate_Pilot_Range", spec: quad("Rate Pilot Range") },
      { name: "HMIREC_Rate_Wheel_Range", spec: quad("Rate Wheel Range") },
    ],
  },
};

export const METRICS_KIND_ORDER: readonly MetricsKind[] = ["wing_bot", "rake_bot", "hmi_record"];
